import React, { useState } from 'react';
import classNames from 'classnames';
import Avatar from './avatar';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveSubTab, setActiveTab, setRightDrawerOpen } from '../../stores/actions/tabs';
import { TABS } from '../../stores/types/tabs';
import { ICONS } from './icon';
import { GreenroomState as GS } from '../../stores/types/types';
import { setSelectedMediaTemplate } from '../../stores/actions/generate-content';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { MEDIA_SETTINGS_TABS } from "../../stores/types/session"
import useMediaItemMemos from '../../hooks/use-media-memos';
import useEpidemicSound from '../../hooks/use-epidemic-sound';

interface Props {
	invites: any,
	size: string,
	alignEnd: boolean,
	expandable?: boolean,
	classnames?: string;
	maxBubbles?: number,
	canDisplayHoverIcon?: boolean,
}

export default function AvatarBubbleRow(props: Props) {
	const dispatch = useDispatch();
	const {
		invites = [],
		size = 'thirtytwo',
		alignEnd,
		expandable = false,
		classnames,
		maxBubbles = 3,
		canDisplayHoverIcon = false
	} = props;
	const { use_title_library: useTitleLibrary } = useSelector((state: GS) => state.SettingsReducer.featureFlags);
	const { isEpidemicSoundBlocked } = useEpidemicSound();
	const [showAll, setShowAll] = useState(false);
	const [hideShowAll, setHideShowAll] = useState(false);
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);

    const {
        currentTemplate,
        currentTemplateMediaItem,
        currentAudioMediaItem,
    } = useMediaItemMemos();

	// with Epidemic Sound off an Epidemic-sourced track has no editor, so hide the
	// edit affordance entirely rather than leaving a click that goes nowhere
	const audioEditBlocked = isEpidemicSoundBlocked(currentAudioMediaItem);

	function hide() {
		setHideShowAll(true);
		setTimeout(() => {
			setShowAll(false);
			setHideShowAll(false);
		}, 300);
	}

	function show() {
		if (expandable) setShowAll(true);
	}

	const avatarOnClick = invites.length === 1 && invites[0].type === 'audio' && !audioEditBlocked
		? (e: any) => {
			if (roomState === ROOM_STATES.EDITING && useTitleLibrary) {
				e.stopPropagation(); // prevent parent onClick from firing
	
				if (currentTemplate && !currentTemplate.data?.strict && currentTemplateMediaItem) {
					dispatch(setSelectedMediaTemplate(currentTemplateMediaItem, currentTemplateMediaItem.template_type ?? ''));
					dispatch(setRightDrawerOpen(true));
					dispatch(setActiveTab(TABS.GENERATE_MEDIA_CONTENT));
					dispatch(setActiveSubTab(TABS.GENERATE_MEDIA_CONTENT, MEDIA_SETTINGS_TABS.AUDIO));
				} else if (currentAudioMediaItem) {
					// without a style-pack template this opens the plain Audio Settings
					// editor against the same item that editor renders
					dispatch(setSelectedMediaTemplate(currentAudioMediaItem, currentAudioMediaItem.type));
					dispatch(setRightDrawerOpen(true));
					dispatch(setActiveTab(TABS.AUDIO_SETTINGS));
				}
			}
		}
		: undefined;

	const hoverIcon = canDisplayHoverIcon && invites.length === 1 && invites[0].type === 'audio' && roomState === ROOM_STATES.EDITING && useTitleLibrary && !audioEditBlocked ? ICONS.EDIT : undefined;

	// find out if invites is array of items with audioOnly attribute
	// let audioOnly = invites && invites.length > 0 && invites[0].audioOnly !== undefined;
	return (
		<div className={classNames('avatar-bubble-row', alignEnd, { 'show-all': showAll }, classnames)} onMouseLeave={hide} data-testid="avatar-bubble-row">
			{invites.length === 0 ? null : invites.map((presenter: any, index: number): JSX.Element | null => {
				if (index < maxBubbles || invites.length === maxBubbles) {
					return (
						<div key={index} className="avatar-container" onClick={avatarOnClick}>
							<Avatar
								user={presenter}
								noBorder={false}
								size={size}
								cursorPointer={invites.length === 1 && presenter.type === 'audio'}
								hoverIcon={hoverIcon}
							/>
						</div>
					);
				}
				return null;
			})}
			{invites.length > maxBubbles && (
				<div className="avatar-container" onClick={show}>
					<div className={classNames('avatar-placeholder', size)} data-testid="avatar-placeholder">
						+{invites.length - maxBubbles}
					</div>
				</div>
			)}
			{showAll && (
				<div className={classNames('show-all-container', { hiding: hideShowAll })}>
					{invites.length === 0 ? null : invites.map((presenter: any, index: number): JSX.Element | undefined => (
						<div key={index} className="avatar-container">
							<Avatar
								user={presenter}
								noBorder={true}
								size={size}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
